import React, { Component } from "react";
import { RouteComponentProps, Link } from "react-router-dom";
import { Table, Tag, Icon } from "antd";

import SearchBusinessPartners from "../../Common/SearchBusinessPartners";
import "../style.scss";
import { SearchPayloadType, PaginationInfoType } from "../types";
import ChangeLogModal from "../../ChangeLog/components/changeLogModal";
import { ViewportType } from "../../App/types";

import {
  BusinessPartnerType,
  StatusType,
  TaxNumberType,
  PseudoroleValueCodeMapType,
  CountryType,
  TargetSystemRoleType,
} from "../../BusinessPartnerDetails/types";
import { SearchStateType } from "../../SearchHome/types";
import { generateSearchUrlParam, getSearchObjFromUrl } from "../../../utils";
import { info } from "winston";

interface SortInfoType {
  field: string;
  order: string;
}

interface FilterInfoType {
  columnKey: string;
  value: string | number;
  bpStatus: any[];
}

interface IProps extends RouteComponentProps<any> {
  statuses: StatusType[];
  searchQuery: string | null;
  paginationInfo: PaginationInfoType;
  searchBPSucceeded: boolean;
  viewport: ViewportType;
  bpList: BusinessPartnerType[];
  pseudoroleValueCodeMap: PseudoroleValueCodeMapType | null;
  countries: CountryType[];
  fetchLookUp: () => any;
  deleteBusinessPartner: (bpId: number) => any;
  searchBP: (searchPayload: SearchPayloadType) => any;
  setMenuKey: (menuKey: string) => any;
  resetSearchBPSucceededFlag: () => any;
}

interface IStates {
  columns: any[];
  sortInfo: null | SortInfoType;
  filterInfo: null | FilterInfoType;
  changeLogVisibility: boolean;
  changeLogData: BusinessPartnerType;
  searchObj: SearchStateType;
}

const capitalize = (content) =>
  content.charAt(0).toUpperCase() + content.slice(1);

const sortOrderMap = {
  asc: "ascend",
  desc: "descend",
  ascend: "ascend",
  descend: "descend",
};

const initialState = {
  columns: [],
  visible: false,
  selectedBpId: null,
  sortInfo: null,
  filterInfo: null,
  changeLogVisibility: false,
  changeLogData: null,
};

class BusinessPartnerListComponent extends Component<IProps, IStates> {
  constructor(props) {
    super(props);
    this.state = {
      ...initialState,
      sortInfo: null,
      filterInfo: null,
      searchObj: null,
    };
  }

  componentDidMount() {
    const { location, setMenuKey, fetchLookUp, statuses, bpList } = this.props;
    const menuKey = location.pathname.replace(/\//gi, "").replace(/-/gi, " ");
    setMenuKey(menuKey);

    // check if lookup data has been loaded or not if not fetch it.
    if (!statuses) {
      fetchLookUp();
    }
    // check if search has already been done or not. if not call it.
    if (bpList === null) {
      // extract searchObj from url
      this.fetchBPUsingURLParams();
    } else {
      // came from search home page
      // set search object from url
      const { searchObj } = getSearchObjFromUrl(location.search);
      this.setState({ searchObj });
    }
  }

  componentDidUpdate(prevProps: IProps) {
    // check if url changed eg. using back button
    const {
      location,
      history,
      searchBPSucceeded,
      resetSearchBPSucceededFlag,
    } = this.props;
    if (
      history.action === "POP" &&
      location.search !== prevProps.location.search
    ) {
      this.fetchBPUsingURLParams();
    }

    if (
      prevProps.searchBPSucceeded !== searchBPSucceeded &&
      searchBPSucceeded
    ) {
      resetSearchBPSucceededFlag();
    }
  }

  componentWillUnmount() {
    const { resetSearchBPSucceededFlag } = this.props;
    resetSearchBPSucceededFlag();
  }

  changeLogToggle = (businessPartner: BusinessPartnerType | null) => {
    const { changeLogVisibility } = this.state;
    if (!changeLogVisibility) {
      this.setState({
        changeLogVisibility: !changeLogVisibility,
        changeLogData: businessPartner,
      });
    } else {
      this.setState({
        changeLogVisibility: !changeLogVisibility,
        changeLogData: null,
      });
    }
  };

  onChange = (pagination, filters, sorter) => {
    const { searchObj } = this.state;
    const searchData = Object.keys(filters)
      .filter(
        (columnKey) => filters[columnKey] && filters[columnKey].length > 0
      )
      .map((columnKey) => ({
        datatype: "number",
        value: filters[columnKey][0].toString(),
        dbKey: capitalize(columnKey),
      }));
    const searchPayload = {
      sortBy:
        sorter && Object.keys(sorter).length > 0
          ? capitalize(sorter.columnKey)
          : "",
      isSortAscending:
        sorter && Object.keys(sorter).length > 0
          ? sorter.order === "ascend"
          : false,
      page: pagination.current,
      pageSize: pagination.pageSize,
      searchData,
      searchObj,
    };
    this.setState({
      filterInfo: Object.keys(filters)
        .filter((key) => filters[key] !== null && filters[key].length > 0)
        .reduce((filterItems, key) => {
          const colKey = capitalize(key);
          filterItems[colKey] = filters[key];
          return filterItems;
        }, {}),
      sortInfo:
        sorter.columnKey && sorter.order
          ? { field: sorter.columnKey, order: sorter.order }
          : null,
    });

    const { searchBP, history } = this.props;
    searchBP(searchPayload);
    const urlParams = generateSearchUrlParam(searchPayload);
    // update url
    history.push(`/search/?${urlParams}`);
  };

  getSearchData = (filters: null | FilterInfoType) => {
    if (!!filters === false) return [];
    return Object.keys(filters)
      .filter((columnKey) => filters[columnKey].length > 0)
      .map((columnKey) => ({
        datatype: "number",
        value: filters[columnKey][0].toString(),
        dbKey: capitalize(columnKey),
      }));
  };

  searchBP = (searchObj: SearchStateType, reset = false) => {
    const { searchBP, history } = this.props;
    if (reset) {
      const searchPayload = {
        sortBy: "",
        isSortAscending: "asc",
        page: 1,
        pageSize: 15,
        searchData: [],
        searchObj,
      };
      searchBP(searchPayload);
      this.setState({
        sortInfo: null,
        filterInfo: null,
        searchObj,
      });
    } else {
      const { paginationInfo } = this.props;
      const { filterInfo, sortInfo } = this.state;

      const searchData = filterInfo
        ? Object.keys(filterInfo).map((key) => ({
            dbKey: key,
            value: filterInfo[key],
          }))
        : [];

      const searchPayload = {
        sortBy: (sortInfo && capitalize(sortInfo.field)) || "",
        isSortAscending: (sortInfo && sortInfo.order) || "asc",
        page: (paginationInfo && paginationInfo.currentPage) || 1,
        pageSize: (paginationInfo && paginationInfo.pageSize) || 15,
        searchData,
        searchObj,
      };

      this.setState({ searchObj });
      searchBP(searchPayload);
    }
    const params = generateSearchUrlParam(searchPayload);
    history.replace(`/search/?${params}`);
  };

  fetchBPUsingURLParams = () => {
    const { location, searchBP } = this.props;
    const {
      searchObj,
      sortInfo,
      paginationInfo,
      filters,
    } = getSearchObjFromUrl(location.search);
    const searchData = filters
      ? Object.keys(filters).map((key) => ({
          dbKey: key,
          value: filters[key],
        }))
      : [];
    const searchPayload = {
      sortBy: (sortInfo && capitalize(sortInfo.field)) || "",
      isSortAscending: (sortInfo && sortInfo.order) || "asc",
      page: (paginationInfo && paginationInfo.currentPage) || 1,
      pageSize: (paginationInfo && paginationInfo.pageSize) || 15,
      searchData,
      searchObj,
    };

    this.setState({
      searchObj,
      filterInfo: filters,
      sortInfo,
    });
    searchBP(searchPayload);
  };

  render() {
    const { paginationInfo, pseudoroleValueCodeMap, statuses } = this.props;
    const { filterInfo, sortInfo } = this.state;
    const columns = [
      {
        title: "BP ID",
        dataIndex: "bpId",
        key: "bpId",
        width: 70,
      },
      {
        title: "InforLN ID",
        dataIndex: "targetSystemRoles",
        key: "targetSystemRoles",
        width: 100,
        render: (targetSystemRoles: TargetSystemRoleType[]): string => {
          if (targetSystemRoles.length === 0) return "";
          const inforLNRoles = targetSystemRoles.filter(
            (item) =>
              item.tsrTargetSystemRefName.toLowerCase() === "inforln" &&
              item.tsrExternalId !== ""
          );
          return inforLNRoles.length > 0 ? inforLNRoles[0].tsrExternalId : "";
        },
      },
      {
        title: "BP Name One",
        dataIndex: "bpName",
        key: "bpName",
        sorter: true,
        sortOrder:
          sortInfo &&
          sortInfo.field.toLowerCase() === "bpname" &&
          sortOrderMap[sortInfo.order],
        width: 190,
      },
      {
        title: "BP Name Two",
        dataIndex: "bpName2",
        key: "bpName2",
        width: 190,
      },
      {
        title: "Role",
        dataIndex: "pbConstantRef",
        key: "pbConstantRef",
        sorter: false,
        width: 100,
        render: (text: any, record: BusinessPartnerType) => {
          return (
            <div>
              {(record.pseudoRoleBpmap.length &&
                pseudoroleValueCodeMap &&
                pseudoroleValueCodeMap.get(
                  record.pseudoRoleBpmap[0].pbConstantRef
                )) ||
                ""}
            </div>
          );
        },
        filters:
          pseudoroleValueCodeMap &&
          Array.from(pseudoroleValueCodeMap).map((value) => {
            return {
              text: value[1],
              value: value[0],
            };
          }),
        filterMultiple: false,
        filteredValue: (filterInfo && filterInfo.PbConstantRef) || null,
      },
      {
        title: "City",
        dataIndex: "bpCityRefName",
        key: "bpCityRefName",
        width: 100,
      },

      {
        title: "Street 1",
        dataIndex: "bpStreet",
        key: "bpStreet",
        width: 100,
      },
      {
        title: "ZIP / Postal",
        dataIndex: "bpPostalcodeRefName",
        key: "bpPostalcodeRefName",
        width: 80,
      },
      {
        title: "Status",
        dataIndex: "bpStatus",
        key: "bpStatus",
        sorter: true,
        width: 120,
        render: (text: number, record: BusinessPartnerType) => {
          let color = "";
          switch (text) {
            case 4: {
              color = "blue";
              break;
            }
            case 5: {
              color = "red";
              break;
            }
            case 6: {
              color = "green";
              break;
            }
            case 7: {
              color = "orange";
              break;
            }
            case 8: {
              color = "";
              break;
            }
            default: {
              color = "";
              break;
            }
          }

          return (
            <>
              <Tag color={color}>{record.bpStatusName}</Tag>
              {record.bpStatus === 7 && record.bpActionName && (
                <div className="action-name">{record.bpActionName}</div>
              )}
            </>
          );
        },
        filters: (statuses || []).map((status) => {
          return {
            text: status.coValue,
            value: status.coId.toString(),
          };
        }),
        filterMultiple: false,
        filteredValue: (filterInfo && filterInfo.BpStatus) || null,
        sortOrder:
          sortInfo &&
          sortInfo.field.toLowerCase() === "bpstatus" &&
          sortOrderMap[sortInfo.order],
      },
      {
        title: "VIES Number",
        dataIndex: "taxNumber",
        key: "taxNumber",
        width: 100,
        render: (tax: TaxNumberType[]) =>
          tax.length > 0 ? tax[0].tnVIESNumber : "",
      },
      {
        title: "Action",
        width: 65,
        fixed: "right",
        render: (text: string, record: BusinessPartnerType) => {
          return (
            <div className="table-action">
              <Link
                to={`/business-partner-details/${record.bpId}`}
                title="Edit"
              >
                <Icon type="edit" style={{ fontSize: "20px" }} />
              </Link>
              {record && record.hasAuditLog && (
                <Icon
                  type="diff"
                  onClick={() => this.changeLogToggle(record)}
                  title="Change Log"
                  style={{ fontSize: "20px", marginLeft: "10px" }}
                />
              )}
            </div>
          );
        },
      },
    ];

    const tableWidth = columns.reduce((totalWidth, col) => {
      totalWidth += col.width ? col.width : 0;
      return totalWidth;
    }, 0);

    const { countries, bpList, viewport } = this.props;
    const { changeLogVisibility, changeLogData } = this.state;
    return (
      <>
        <div className="bp-list">
          <SearchBusinessPartners
            searchBP={this.searchBP}
            countries={countries}
          />
          <Table
            size="small"
            rowKey="bpId"
            columns={columns}
            dataSource={bpList}
            onChange={this.onChange}
            className="bp-table"
            scroll={{ x: tableWidth }}
            pagination={{
              current: paginationInfo.currentPage,
              total: paginationInfo.totalRecords,
              pageSize: paginationInfo.pageSize,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} Business Partners`,
              pageSizeOptions: ["15", "30", "50", "100"],
            }}
          />
          {changeLogVisibility && (
            <ChangeLogModal
              close={this.changeLogToggle}
              changeLogList={changeLogData}
              viewport={viewport}
            />
          )}
        </div>
      </>
    );
  }
}

export default BusinessPartnerListComponent;
